import type { CustomMapAction, EditMapAction } from "domains/createMapDetail/actionTypes";
import { CreateMapTypes } from "domains/createMapDetail/actionTypes";
import type { CreateMapState } from "domains/createMapDetail/types";
import { FakeProgressStatuses } from "domains/createMapDetail/types";
import type { CustomKeyIssueAction } from "domains/customKeyIssue/actionTypes";
import { CustomKeyIssueTypes } from "domains/customKeyIssue/actionTypes";
import type { NewCustomDimension } from "domains/dimension/dimension.types";
import type { TopicDetailAction } from "domains/topicDetail/actionTypes";
import { TopicDetailType } from "domains/topicDetail/actionTypes";
import { getPersistedImages } from "entities/topics/helpers/getPersistedImages";

const initialState: CreateMapState = {
  isPending: false,
  name: "",
  description: "",
  keyIssueIds: [],
  newCustomKeyIssues: [],
  isDraggingKeyIssue: false,
  isAIGeneratedModeOn: false,
  AIGeneratedMapProgress: { status: FakeProgressStatuses["Not started"] },
  isAIGeneratedMapFetching: false,
  AIGeneratedMapErrorCode: null,
};

export const createMapReducer = (
  state = initialState,
  action: CustomKeyIssueAction | CustomMapAction | EditMapAction | TopicDetailAction,
): CreateMapState => {
  switch (action.type) {
    case CreateMapTypes.INIT_FULFILLED:
      return {
        ...state,
        data: { ...action.payload, dimensionIds: action.payload.dimensions?.map(({ id }) => id) },
        keyIssueIds: action.payload.dimensions?.map((topic) => topic.id) ?? [],
        name: action.payload.name,
        description: action.payload.description ?? "",
        newCustomKeyIssues: [],
      };
    case CreateMapTypes.SET: {
      const { name, description, invitations, is_public } = action.meta;
      return {
        ...state,
        name: name ?? state.name,
        description: description ?? state.description,
        data: {
          ...state.data,
          invitations: invitations ?? state.data?.invitations,
          is_public: is_public ?? state.data?.is_public,
        },
      };
    }
    case CreateMapTypes.SET_IMAGE:
      return {
        ...state,
        image: action.payload.image,
      };
    case CreateMapTypes.UPLOAD: {
      return {
        ...state,
        isPending: true,
      };
    }
    case CreateMapTypes.UPLOAD_FULFILLED: {
      const existingImages = state.data?.images;
      const images =
        existingImages === null || existingImages === undefined
          ? action.payload.images
          : getPersistedImages(action.payload.images, existingImages);
      return {
        ...state,
        data: {
          ...state.data,
          description: action.payload.description,
          dimensionIds: action.payload.dimensionIds,
          images,
          name: action.payload.name,
        },
        image: undefined,
        isPending: false,
        newCustomKeyIssues: [],
      };
    }
    case CreateMapTypes.UPLOAD_REJECTED:
      return {
        ...state,
        isPending: false,
      };
    case CreateMapTypes.ADD_KEY_ISSUE_FULFILLED: {
      const issues = state.keyIssueIds.filter((id) => id !== action.dimensionId);
      return {
        ...state,
        keyIssueIds: [...issues, action.dimensionId],
      };
    }
    case CreateMapTypes.REMOVE_KEY_ISSUE:
      return {
        ...state,
        keyIssueIds: state.keyIssueIds.filter((id) => id !== action.payload.keyIssueId),
        newCustomKeyIssues: state.newCustomKeyIssues.filter(({ id }) => id !== action.payload.keyIssueId),
      };
    case CreateMapTypes.REMOVE_ALL_KEY_ISSUES:
      return {
        ...state,
        keyIssueIds: [],
        newCustomKeyIssues: [],
      };
    case CreateMapTypes.ADD_NEW_CUSTOM_KEY_ISSUE: {
      const dimension = action.payload;
      const otherNewCustomDimensions = state.newCustomKeyIssues.filter(({ id }) => id !== dimension.id);
      const otherDimensions = state.keyIssueIds.filter((id) => id !== dimension.id);

      return {
        ...state,
        keyIssueIds: [...otherDimensions, dimension.id],
        newCustomKeyIssues: [...otherNewCustomDimensions, dimension],
      };
    }
    case CustomKeyIssueTypes.CREATE_FULFILLED: {
      const { dimensionId } = action.payload;
      if (state.data?.dimensionIds === undefined || dimensionId === undefined) return state;

      const storedDimensionIds = state.data.dimensionIds.filter((id) => id !== action.payload.dimensionId);
      return { ...state, data: { ...state.data, dimensionIds: [...storedDimensionIds, dimensionId] } };
    }
    case CreateMapTypes.SET_DRAGGING:
      return {
        ...state,
        isDraggingKeyIssue: action.meta.isDragging,
      };
    case CreateMapTypes.ENTER_AI_GENERATED_MODE:
      return {
        ...state,
        isAIGeneratedModeOn: true,
        AIGeneratedMapErrorCode: null,
      };
    case CreateMapTypes.EXIT_AI_GENERATED_MODE:
      return {
        ...state,
        isAIGeneratedModeOn: false,
        isAIGeneratedMapFetching: false,
        AIGeneratedMapErrorCode: null,
      };
    case CreateMapTypes.GET_AI_GENERATED_PROGRESS_SUCCESS:
      return {
        ...state,
        AIGeneratedMapProgress: action.meta,
      };
    case CreateMapTypes.SET_NEW_CUSTOM_DIMENSION_TOPICS:
      return {
        ...state,
        newCustomKeyIssues: state.newCustomKeyIssues.reduce<NewCustomDimension[]>(
          (accumulator, next) => [
            ...accumulator,
            next.id === action.payload.id
              ? { ...next, topic_ids: action.payload.topics.map((topic) => topic.id) }
              : next,
          ],
          [],
        ),
      };
    case TopicDetailType.INIT:
      return {
        ...state,
        AIGeneratedMapProgress: { status: FakeProgressStatuses["Not started"] },
      };
    case CreateMapTypes.RESET:
      return initialState;
    case TopicDetailType.GET_AI_GENERATED_DETAILS:
      return {
        ...state,
        isAIGeneratedMapFetching: true,
        AIGeneratedMapErrorCode: null,
      };
    case TopicDetailType.GET_AI_GENERATED_DETAILS_SUCCESS:
      return {
        ...state,
        description: action.payload.description ?? "",
        isAIGeneratedMapFetching: false,
        isAIGeneratedModeOn: false,
        keyIssueIds: action.payload.dimensions?.map((topic) => topic.id) ?? [],
        name: action.payload.name,
        newCustomKeyIssues: [],
      };
    case TopicDetailType.GET_AI_GENERATED_DETAILS_FAILURE:
      return {
        ...state,
        isAIGeneratedMapFetching: false,
      };
    case TopicDetailType.GET_AI_GENERATED_DETAILS_NOT_VALID_PROMPT:
      return {
        ...state,
        isAIGeneratedMapFetching: false,
        AIGeneratedMapErrorCode: action.errorCode,
      };
    default:
      return state;
  }
};
